import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Pencil, History, ChevronDown, RotateCcw } from 'lucide-react';

interface PromptTemplate {
  id: string;
  module: string;
  variant: string;
  version: number;
  prompt_text: string;
  is_active: boolean;
  created_at: string;
  tenant_id: string | null;
}

interface PromptGroup {
  module: string;
  variant: string;
  active: PromptTemplate | null;
  history: PromptTemplate[];
}

export const PromptManager = () => {
  const { tenantId } = useAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PromptGroup | null>(null);
  const [draftText, setDraftText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('prompt_templates')
      .select('*')
      .order('module', { ascending: true })
      .order('variant', { ascending: true })
      .order('version', { ascending: false });
    if (!error) setTemplates((data ?? []) as PromptTemplate[]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo<PromptGroup[]>(() => {
    const byKey = new Map<string, PromptTemplate[]>();
    for (const t of templates) {
      const key = `${t.module}::${t.variant}`;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push(t);
    }
    return [...byKey.entries()].map(([key, rows]) => {
      const [module, variant] = key.split('::');
      return {
        module,
        variant,
        active: rows.find((r) => r.is_active) ?? null,
        history: rows,
      };
    });
  }, [templates]);

  const openEdit = (group: PromptGroup) => {
    setEditing(group);
    setDraftText(group.active?.prompt_text ?? '');
  };

  // Saving creates a new version (rather than mutating the active row) so
  // the version history stays a real audit trail and rollback is just
  // reactivating an older version, not reconstructing lost text.
  const saveNewVersion = async () => {
    if (!editing || !draftText.trim()) return;
    setSaving(true);
    const nextVersion = Math.max(0, ...editing.history.map((h) => h.version)) + 1;

    const { error: insertError } = await supabase.from('prompt_templates').insert({
      module: editing.module,
      variant: editing.variant,
      version: nextVersion,
      prompt_text: draftText.trim(),
      is_active: true,
      tenant_id: tenantId,
    });
    if (insertError) {
      toast({ title: 'Save failed', description: insertError.message, variant: 'destructive' });
      setSaving(false);
      return;
    }

    if (editing.active) {
      await supabase
        .from('prompt_templates')
        .update({ is_active: false })
        .eq('id', editing.active.id);
    }

    setSaving(false);
    toast({ title: `Saved v${nextVersion}`, description: `${editing.module} / ${editing.variant} is now active.` });
    setEditing(null);
    load();
  };

  const activateVersion = async (group: PromptGroup, target: PromptTemplate) => {
    if (group.active) {
      await supabase.from('prompt_templates').update({ is_active: false }).eq('id', group.active.id);
    }
    const { error } = await supabase.from('prompt_templates').update({ is_active: true }).eq('id', target.id);
    if (error) {
      toast({ title: 'Activation failed', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: `Rolled back to v${target.version}`, description: `${group.module} / ${group.variant}` });
    load();
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Prompt Manager</CardTitle>
          <CardDescription>
            System prompts for every AI-generating function. Editing creates a new version — old
            versions stay available to roll back to.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-muted-foreground text-sm">
              <p>No prompt templates found.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {groups.map((group) => (
                <div key={`${group.module}::${group.variant}`} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{group.module}</span>
                        <Badge variant="secondary" className="text-xs">{group.variant}</Badge>
                        {group.active && (
                          <span className="text-xs text-muted-foreground">v{group.active.version}</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {group.active?.prompt_text ?? 'No active version'}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" className="gap-2 shrink-0" onClick={() => openEdit(group)}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                  </div>

                  {group.history.length > 1 && (
                    <Collapsible className="mt-3">
                      <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        <History className="h-3 w-3" />
                        Version history ({group.history.length})
                        <ChevronDown className="h-3 w-3" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 space-y-1.5">
                        {group.history.map((v) => (
                          <div
                            key={v.id}
                            className="flex items-center justify-between rounded border border-border/50 bg-muted/30 px-3 py-1.5 text-xs"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-medium">v{v.version}</span>
                              {v.is_active && <Badge className="text-[10px] h-4">active</Badge>}
                              <span className="text-muted-foreground truncate">{v.prompt_text.slice(0, 80)}</span>
                            </div>
                            {!v.is_active && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 gap-1 text-xs shrink-0"
                                onClick={() => activateVersion(group, v)}
                              >
                                <RotateCcw className="h-3 w-3" /> Roll back
                              </Button>
                            )}
                          </div>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Edit prompt — {editing?.module} / {editing?.variant}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            rows={16}
            className="font-mono text-sm"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveNewVersion} disabled={saving || !draftText.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Save as new version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PromptManager;
